<template>
  <div class="ordering-page">
    <OrderHeader :breadcrumbs="breadcrumbs" />
    <div v-if="loading" class="page-loading">{{ $t("services.loading") }}</div>
    <div class=" page-loading" v-else-if="is403Error">
      {{ $t("services.error403") }}
    </div>
    <div v-else class="ordering-page__inner container">
      <div v-if="company" class="ordering-page__company-card">
        <img
          class="ordering-page__company-img"
          v-if="company.image"
          :src="company.image.thumbnail_path | image"
          :alt="company | localeField('name')"
        />
        <span>{{ company | localeField("name") }}</span>
      </div>
      <div class="ordering-page__wrapper form form__col-3">
        <InputLabel class="form__input">
          <template #label>
            {{ $t("services.standTitle") }}
          </template>
          <input v-model="currentStand.title" disabled />
        </InputLabel>
        <InputLabel class="form__input">
          <template #label>
            {{ $t("services.ordering.pavilion") }}
          </template>
          <input v-model="form.pavilion.value" disabled />
        </InputLabel>
        <InputLabel class="form__input">
          <template #label>
            {{ $t("services.ordering.hall") }}
          </template>
          <input v-model="form.hall.value" disabled />
        </InputLabel>
      </div>
      <div class="ordering-page__counterparties">
        <span class="ordering-page__counterparties-title">
          {{ $t("services.ordering.counterparty.title") }}
        </span>
        <OrderingCounterpartyComponent
          v-for="(item, i) in counterparties"
          :key="i"
          :data="item"
          :index="i"
          ref="counterparty"
          :active="selectedCounterpartyIndex === i"
          @change="handleCounterpartyChange($event, i)"
          @input="handleCounterpartyInput($event, i)"
        />
        <PrimaryButton
          v-if="canAddCounterparty"
          @click="addCounterparty"
          class="ordering-page__counterparties-btn"
        >
          <icon name="plus-white" />
          {{ $t("services.ordering.counterparty.addBtn") }}
        </PrimaryButton>
      </div>
      <div class="ordering-page-customer-info">
        <span class="ordering-page-customer-info__title">
          {{ $t("services.ordering.customer.title") }}
        </span>
        <div class="ordering-page-customer-info__form ordering-page__wrapper">
          <div class="form form__col-3">
            <InputLabel class="form__input" error-position-static>
              <template #label>
                {{ $t("services.ordering.customer.lastname") }}
                <span class="form__required">*</span>
              </template>

              <input v-model="form.customer_lastname.value" />
              <template slot="error" v-if="form.customer_lastname.error">
                {{ form.customer_lastname.error }}
              </template>
            </InputLabel>
            <InputLabel class="form__input" error-position-static>
              <template #label>
                {{ $t("services.ordering.customer.name") }}
                <span class="form__required">*</span>
              </template>

              <input v-model="form.customer_name.value" />
              <template slot="error" v-if="form.customer_name.error">
                {{ form.customer_name.error }}
              </template>
            </InputLabel>
            <InputLabel class="form__input" error-position-static>
              <template #label>
                {{ $t("services.ordering.customer.middlename") }}
              </template>

              <input v-model="form.customer_middlename.value" />
              <template slot="error" v-if="form.customer_middlename.error">
                {{ form.customer_middlename.error }}
              </template>
            </InputLabel>
          </div>
          <div class="form__col form__col-2">
            <InputLabel class="form__input" error-position-static>
              <template #label>
                {{ $t("services.ordering.customer.email") }}
                <span class="form__required">*</span>
              </template>

              <input v-model="form.customer_email.value" disabled />
              <template slot="error" v-if="form.customer_email.error">
                {{ form.customer_email.error }}
              </template>
            </InputLabel>
            <InputLabel class="form__input" error-position-static>
              <template #label>
                {{ $t("services.ordering.customer.phone") }}
                <span class="form__required">*</span>
              </template>
              <the-mask
                mask="+###############"
                name="customer_phone"
                placeholder="+7 900 000 00 00"
                v-model="form.customer_phone.value"
                v-validate="{
                  regex: /^[0-9]*$/,
                  min: 7
                }"
              />
              <template slot="error" v-if="errors.has('customer_phone')">
                {{
                  getValidateErrorWithoutName(
                    errors.first("customer_phone"),
                    "customer_phone"
                  )
                }}
              </template>
            </InputLabel>
          </div>
        </div>
      </div>
      <div class="ordering-page-comment">
        <span class="textarea__title">
          {{ $t("services.ordering.comment") }}
        </span>
        <textarea-autosize
          v-model="form.comment.value"
          class="input textarea"
          :placeholder="$t('services.ordering.commentInputPlaceholder')"
          :min-height="100"
        />
        <span class="textarea__description">
          {{ $t("services.ordering.commentSubtitle") }}
        </span>
      </div>
      <div class="ordering-page__checkbox" v-if="isSomeServiceRequired">
        <checkbox v-model="form.need_contract.value" disabled checked>
          {{ $t("services.ordering.needContract") }}
        </checkbox>
        <span>
          {{ $t("services.ordering.requiredContract") }}
        </span>
      </div>
      <div class="ordering-page__checkbox">
        <checkbox v-model="form.isEdo.value">
          {{ $t("services.ordering.is_edo") }}
        </checkbox>
        <p
          class="ordering-page__checkbox-description"
          v-if="locale === 'ru' && form.isEdo.value"
        >
          Электронный документ, заверенный квалифицированной электронной
          подписью в рамках системы ЭДО, обладает юридической силой
          оригинального документа. Оформление бумажной копии закрывающих
          документов не требуется.
        </p>
      </div>
      <div class="ordering-page-footer">
        <div>
          <div class="ordering-page-footer__info">
            <span class="ordering-page-footer__info-title">
              {{ $t("services.servicesSumWithVat") }}:
            </span>
            <span class="ordering-page-footer__info-price">
              {{ cartSum() | formatPrice({ store: $store }) }}
            </span>
          </div>
        </div>
        <PrimaryButton
          @click="submit"
          class="ordering-page-footer__btn"
          :disabled="!isOrderingAvailable"
          :is-loading="submitLoading"
        >
          {{ $t("services.ordering.submitBtn") }}
        </PrimaryButton>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import apiServices from "@/api/services";
import getError from "@/helpers/error";
import diffInSeconds from "@/helpers/diffInSeconds";
import OrderHeader from "@/components/Custom/OrderHeader.vue";
import OrderingCounterpartyComponent from "@/components/Custom/OrderingCounterpartyComponent.vue";
import { PrimaryButton, Checkbox, InputLabel } from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";

export default {
  name: "OrderPage",
  components: {
    OrderingCounterpartyComponent,
    OrderHeader,
    PrimaryButton,
    Checkbox,
    InputLabel
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      selectedCounterparty: null,
      selectedCounterpartyIndex: null,
      canAddCounterparty: true,
      counterpartyError: null,
      is403Error: false,
      list: [],
      form: {
        stand: {
          value: null,
          error: null
        },
        pavilion: {
          value: null,
          error: null
        },
        hall: {
          value: null,
          error: null
        },
        customer_name: {
          value: null,
          error: null
        },
        customer_middlename: {
          value: null,
          error: null
        },
        customer_lastname: {
          value: null,
          error: null
        },
        customer_email: {
          value: null,
          error: null
        },
        customer_phone: {
          value: null,
          error: null
        },
        comment: {
          value: null,
          error: null
        },
        need_contract: {
          value: null,
          error: null
        },
        isEdo: {
          value: false,
          error: null
        }
      },
      breadcrumbs: [
        {
          title_rus: "Выставка",
          title_eng: "Exhibition",
          route: { name: "participant", params: { id: this.$route.params.id } }
        },
        {
          title_rus: "Заказ услуг",
          title_eng: "Services order",
          route: { name: "categories", params: { id: this.$route.params.id } }
        },
        { title_rus: "Корзина", title_eng: "Cart", route: { name: "cart" } }
      ]
    };
  },
  created() {
    this.load();
  },
  mounted() {
    setTimeout(() => {
      this.form.customer_email.value = this.user.email;
      this.form.customer_name.value = this.user.name;
      this.form.customer_middlename.value = this.user.middlename;
      this.form.customer_lastname.value = this.user.lastname;
      this.form.customer_phone.value = this.user.phone;
      this.form.need_contract.value = this.isSomeServiceRequired;
      if (this.currentStand) {
        this.handleStandSelect(this.currentStand);
      }
    }, 0);
  },
  watch: {
    isSomeServiceRequired(value) {
      this.form.need_contract.value = value;
    }
  },
  computed: {
    ...mapState("event", ["event", "counterparties", "currentStand"]),
    ...mapState("auth", ["user"]),
    ...mapState("services", ["cart"]),
    ...mapGetters("services", [
      "cartSum",
      "currency",
      "isCartEmpty",
      "isSomeServiceRequired"
    ]),
    locale() {
      return i18n.locale;
    },
    company() {
      return this.event ? this.event.company : null;
    },

    stands() {
      return (
        this.event?.stands?.map(stand => {
          return { ...stand, selected: stand.id === this.currentStand?.id };
        }) || []
      );
    },

    isOrderingAvailable() {
      return this.event.exhibition.is_allowed_create_additional_order;
    }
  },
  methods: {
    async load() {
      try {
        this.loading = true;
        await this.$store.dispatch("event/get", this.$route.params.id);
        await this.$store.dispatch(
          "event/getCounterparties",
          this.$route.params.id
        );
        this.list = this.counterparties;
      } catch (e) {
        this.is403Error = e.error_data?.response?.status === 403;
      } finally {
        this.loading = false;
      }
    },
    getValidateErrorWithoutName(error, name) {
      return error.replace(`${name} `, "");
    },
    diffInSeconds,
    handleCounterpartyChange(data, index) {
      this.selectedCounterparty = data;
      this.selectedCounterpartyIndex = index;
    },
    handleCounterpartyInput(data, index) {
      // если контрагент уже выбран, то изменяем его данные
      if (index === this.selectedCounterpartyIndex) {
        this.selectedCounterparty = data;
      }
    },
    handleStandSelect(stand) {
      if (stand) {
        this.form.stand.value = stand;
        this.form.pavilion.value = this.$options.filters.localeField(
          stand.pavilion.parent,
          "title"
        );
        this.form.hall.value = this.$options.filters.localeField(
          stand.pavilion,
          "title"
        );
        this.$store.dispatch("event/setStand", stand);
      }
    },
    addCounterparty() {
      this.counterparties.push({});
      this.canAddCounterparty = false;
    },
    submit() {
      if (this.isCartEmpty) {
        this.$modal.show("alert-modal", {
          data: {
            title_rus: "Ошибка!",
            title_eng: "Error!",
            text_rus: "Ваша корзина пуста",
            text_eng: "Your cart is empty"
          }
        });
        return;
      }

      if (!this.submitLoading) {
        this.submitLoading = true;
        let counterpartyData = {};
        if (this.selectedCounterparty) {
          counterpartyData = {
            counterparty_name: this.selectedCounterparty.counterparty_name
              .value,
            counterparty_inn: this.selectedCounterparty.counterparty_inn.value,
            counterparty_kpp: this.selectedCounterparty.counterparty_kpp.value,
            counterparty_legal_address: this.selectedCounterparty
              .counterparty_legal_address.value,
            counterparty_address: this.selectedCounterparty.counterparty_address
              .value,
            counterparty_code: this.selectedCounterparty.counterparty_code
              .value,
            bank: this.selectedCounterparty.bank.value,
            bank_account: this.selectedCounterparty.bank_account.value,
            correspondent_account: this.selectedCounterparty
              .correspondent_account.value
          };
        }
        let data = {
          stand: this.form.stand.value
            ? this.form.stand.value.title
            : undefined,
          need_contract: this.form.need_contract.value || undefined,
          is_edo: this.form.isEdo.value,
          customer_name: this.form.customer_name.value || undefined,
          customer_middlename: this.form.customer_middlename.value || undefined,
          customer_lastname: this.form.customer_lastname.value || undefined,
          customer_email: this.form.customer_email.value || undefined,
          customer_phone: this.form.customer_phone.value || undefined,
          currency: this.currency ? this.currency.code : undefined,
          comment: this.form.comment.value || undefined,
          ...counterpartyData
        };
        apiServices
          .orderCreate(this.$route.params.id, data)
          .then(({ data }) => {
            this.$modal.show("order-success", { data: data });
          })
          .catch(error => {
            this.$store.dispatch("common/setErrorMessage", getError(error));
          })
          .finally(() => {
            this.submitLoading = false;
          });
      }
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
.ordering-page {
  min-height: 100%;
  flex-grow: 1;
  background-color: var(--gray, #f9f9f9);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  .container {
    width: 100%;
    @media (max-width: 740px) {
      width: auto;
      margin-left: 30px;
      margin-right: 30px;
    }
  }

  &__inner {
    padding: 50px 0;
    flex-grow: 1;
    @media (max-width: 540px) {
      padding: 30px 0;
    }
  }

  &__company-card {
    width: 100%;
    max-width: 245px;
    display: flex;
    align-items: center;
    column-gap: 15px;
    padding: 15px;
    border-radius: 2px;
    background: var(--white, #fff);

    color: var(--dark, #262626);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 22px; /* 157.143% */

    @media (max-width: 440px) {
      max-width: none;
    }
  }

  &__company-img {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }

  &__counterparties {
    display: flex;
    padding: 30px;
    flex-direction: column;
    border-radius: 2px;
    border: 1px dashed var(--gray-darker, #d5dae5);
    margin-top: 30px;
    @media (max-width: 640px) {
      padding: 15px;
    }

    &-title {
      color: var(--gray-dark, #a3a8b3);
      font-size: 1.375rem;
      font-weight: 700;
      line-height: 28px;
      @media (max-width: 640px) {
        font-size: 1.25rem;
        font-weight: 500;
      }
    }

    &-btn {
      padding: 9px 20px;
      max-width: fit-content;
      margin-top: 20px;
    }
  }

  &__wrapper {
    display: flex;
    padding: 30px;
    border-radius: 2px;
    background: var(--white, #fff);
    margin-top: 30px;
    flex-direction: column;

    @media (max-width: 640px) {
      padding: 15px;
    }
  }

  &-customer-info {
    margin-top: 30px;

    &__form {
      @media (max-width: 640px) {
        margin-top: 20px;
      }
    }

    &__title {
      color: var(--gray-dark, #a3a8b3);
      font-size: 1.375rem;
      font-weight: 700;
      line-height: 28px; /* 127.273% */

      @media (max-width: 640px) {
        font-size: 1.25rem;
        font-weight: 500;
      }
    }
  }

  &-comment {
    display: flex;
    flex-direction: column;
    padding: 15px;
    border-radius: 2px;
    background: var(--white, #fff);
    margin-top: 30px;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 18px;

    & .textarea {
      width: 100%;
      padding: 10px 15px;
      border-radius: 2px;
      border: 1px solid var(--gray-darker, #d5dae5);
      margin: 5px 0;
      outline: none;

      &::placeholder {
        color: var(--gray-dark, #a3a8b3);
      }

      &__title {
        color: var(--dark-light, #6d7179);
      }

      &__description {
        color: var(--gray-dark, #a3a8b3);
      }
    }
  }

  &__checkbox {
    margin-top: 30px;
    display: flex;
    flex-direction: column;

    & span {
      color: #6d7179;
      font-size: 0.75rem;
      font-weight: 400;
      line-height: 18px; /* 150% */
      padding-left: 36px;
      margin-top: 5px;
    }
    &-description {
      @include description();
      margin-top: 10px;
    }
  }

  &__download {
    width: fit-content;
    margin-top: 30px;
    display: flex;
    column-gap: 10px;
    color: var(--dark-light, #6d7179);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 18px; /* 150% */
    cursor: pointer;

    & .svg-icon {
      width: 20px;
      height: 20px;
    }
  }

  &-footer {
    margin-top: 30px;
    display: flex;
    column-gap: 10px;
    align-items: center;

    @media (max-width: 640px) {
      flex-direction: column;
      align-items: flex-start;
    }

    &__info {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      @media (max-width: 540px) {
        margin-bottom: 15px;
      }

      &-title {
        color: var(--dark-light, #6d7179);
        font-feature-settings: "clig" off, "liga" off;
        font-size: 1rem;
        font-weight: 500;
        line-height: 24px;
        margin-right: 10px;
      }

      &-price {
        color: var(--dark, #262626);
        font-feature-settings: "clig" off, "liga" off;
        font-size: 1.375rem;
        font-weight: 700;
        line-height: 28px;
      }

      &-subvalue {
        color: var(--dark, #262626);
        font-feature-settings: "clig" off, "liga" off;
        font-size: 1.1rem;
        font-weight: 700;
        line-height: 25px;
      }
    }

    &__btn {
      margin-left: auto;

      @media (max-width: 640px) {
        margin-top: 15px;
        width: 100%;
      }
    }
  }
}

.form {
  column-gap: 20px;

  &__required {
    color: var(--orange, #f6931d);
  }

  &__input:not(:first-child) {
    @media (max-width: 640px) {
      margin-top: 20px;
    }
  }

  &__col {
    display: flex;
    column-gap: 20px;
    margin-top: 20px;
  }

  &__col-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    @media (max-width: 640px) {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  &__col-2 {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    @media (max-width: 640px) {
      grid-template-columns: repeat(1, 1fr);
    }
  }
}
</style>
